import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Container, Form, Modal, Table } from 'react-bootstrap';
import { onValue, push, ref, remove, set, update } from 'firebase/database';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { database, storage } from '../firebase';

interface LearningCard {
  id: string;
  frontText?: string;
  frontImageUrl?: string;
  frontImagePath?: string;
  backText?: string;
  backImageUrl?: string;
  backImagePath?: string;
  rating?: number;
  createdAt?: number;
  updatedAt?: number;
}

interface LearningCardSetMeta {
  name: string;
}

const LearningCardSet = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { setId } = useParams();

  const [setMeta, setSetMeta] = useState<LearningCardSetMeta | null>(null);
  const [cards, setCards] = useState<LearningCard[]>([]);
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [newCard, setNewCard] = useState({
    frontText: '',
    frontImageFile: null as File | null,
    backText: '',
    backImageFile: null as File | null,
    rating: 3
  });

  const [showBulkAddModal, setShowBulkAddModal] = useState(false);
  const [bulkSide, setBulkSide] = useState<'front' | 'back'>('front');
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);

  const [editingCard, setEditingCard] = useState<LearningCard | null>(null);
  const [editFrontImageFile, setEditFrontImageFile] = useState<File | null>(null);
  const [editBackImageFile, setEditBackImageFile] = useState<File | null>(null);
  const [removeEditFrontImage, setRemoveEditFrontImage] = useState(false);
  const [removeEditBackImage, setRemoveEditBackImage] = useState(false);
  const [hoveredCardRating, setHoveredCardRating] = useState<{ cardId: string; rating: number } | null>(null);

  useEffect(() => {
    if (!currentUser || !setId) return;

    const metaRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}`);
    const unsubscribeMeta = onValue(metaRef, (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setSetMeta(null);
        return;
      }
      setSetMeta({ name: data?.name || '' });
    });

    const cardsRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards`);
    const unsubscribeCards = onValue(cardsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.entries(data).map(([id, value]: [string, any]) => ({
        id,
        frontText: value?.frontText,
        frontImageUrl: value?.frontImageUrl,
        frontImagePath: value?.frontImagePath,
        backText: value?.backText,
        backImageUrl: value?.backImageUrl,
        backImagePath: value?.backImagePath,
        rating: value?.rating,
        createdAt: value?.createdAt,
        updatedAt: value?.updatedAt
      }));
      list.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
      setCards(list);
    });

    return () => {
      unsubscribeMeta();
      unsubscribeCards();
    };
  }, [currentUser, setId]);

  const canSubmitNewCard = useMemo(() => {
    const hasFront = newCard.frontText.trim().length > 0 || !!newCard.frontImageFile;
    const hasBack = newCard.backText.trim().length > 0 || !!newCard.backImageFile;
    return hasFront && hasBack;
  }, [newCard]);

  const tryExtractStoragePathFromUrl = (url?: string) => {
    if (!url) return null;
    try {
      const parts = url.split('?')[0].split('/o/');
      if (parts.length < 2) return null;
      return decodeURIComponent(parts[1]);
    } catch {
      return null;
    }
  };

  const handleSwapFrontBack = async (card: LearningCard) => {
    if (!currentUser || !setId) return;
    if (!window.confirm('Vorder- und Rückseite wirklich tauschen?')) return;

    try {
      const cardRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards/${card.id}`);
      await update(cardRef, {
        frontText: (card.backText || '').trim() || null,
        frontImageUrl: (card.backImageUrl || '').trim() || null,
        frontImagePath: (card.backImagePath || '').trim() || null,
        backText: (card.frontText || '').trim() || null,
        backImageUrl: (card.frontImageUrl || '').trim() || null,
        backImagePath: (card.frontImagePath || '').trim() || null,
        updatedAt: Date.now()
      });
      await touchSetUpdatedAt();
      setAlertMessage({ type: 'success', message: 'Vorder- und Rückseite getauscht.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Tauschen der Karte.' });
    }
  };

  const handleUpdateCardRating = async (cardId: string, rating: number) => {
    if (!currentUser || !setId) return;
    try {
      const cardRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards/${cardId}`);
      await update(cardRef, { rating, updatedAt: Date.now() });
      await touchSetUpdatedAt();
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Speichern der Bewertung.' });
      setTimeout(() => setAlertMessage(null), 2500);
    }
  };

  const canSubmitBulkAdd = useMemo(() => bulkFiles.length > 0 && !isBulkAdding, [bulkFiles.length, isBulkAdding]);

  const handleBulkAdd = async () => {
    if (!currentUser || !setId) return;
    if (bulkFiles.length === 0) return;

    setIsBulkAdding(true);
    setBulkProgress({ current: 0, total: bulkFiles.length });

    let successCount = 0;
    let failedCount = 0;

    try {
      for (let i = 0; i < bulkFiles.length; i += 1) {
        const file = bulkFiles[i];
        setBulkProgress({ current: i + 1, total: bulkFiles.length });

        try {
          const cardsRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards`);
          const newCardRef = push(cardsRef);
          const cardId = newCardRef.key;
          if (!cardId) throw new Error('Missing card id');

          const now = Date.now();

          let frontImageUrl: string | null = null;
          let frontImagePath: string | null = null;
          let backImageUrl: string | null = null;
          let backImagePath: string | null = null;

          if (bulkSide === 'front') {
            const uploaded = await uploadCardImage(file, 'front', cardId);
            frontImageUrl = uploaded.url;
            frontImagePath = uploaded.path;
          } else {
            const uploaded = await uploadCardImage(file, 'back', cardId);
            backImageUrl = uploaded.url;
            backImagePath = uploaded.path;
          }

          const frontText = bulkSide === 'back' ? fileBaseName(file) : null;
          const backText = bulkSide === 'front' ? fileBaseName(file) : null;

          await set(newCardRef, {
            frontText,
            frontImageUrl,
            frontImagePath,
            backText,
            backImageUrl,
            backImagePath,
            rating: 3,
            createdAt: now,
            updatedAt: now
          });

          successCount += 1;
        } catch (e) {
          failedCount += 1;
          console.error(e);
        }
      }

      await touchSetUpdatedAt();

      setShowBulkAddModal(false);
      setBulkFiles([]);
      setBulkSide('front');

      if (failedCount === 0) {
        setAlertMessage({ type: 'success', message: `${successCount} Karten hinzugefügt.` });
      } else {
        setAlertMessage({ type: 'danger', message: `${successCount} Karten hinzugefügt, ${failedCount} fehlgeschlagen.` });
      }
      setTimeout(() => setAlertMessage(null), 3500);
    } finally {
      setIsBulkAdding(false);
      setBulkProgress(null);
    }
  };

  const deleteStorageObjectIfExists = async (pathOrUrl?: string | null) => {
    if (!pathOrUrl) return;
    const path = pathOrUrl.includes('/o/') ? tryExtractStoragePathFromUrl(pathOrUrl) : pathOrUrl;
    if (!path) return;
    try {
      await deleteObject(storageRef(storage, path));
    } catch (e) {
      console.warn('Failed to delete storage object', e);
    }
  };

  const fileBaseName = (file: File) => {
    const name = file.name || '';
    const lastDot = name.lastIndexOf('.');
    const base = lastDot > 0 ? name.slice(0, lastDot) : name;
    return base.trim();
  };

  const uploadCardImage = async (file: File, side: 'front' | 'back', cardId: string) => {
    if (!currentUser || !setId) throw new Error('Missing user or set');
    const safeName = (file.name || 'image').replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `learning_cards/${currentUser.uid}/sets/${setId}/cards/${cardId}/${side}/${Date.now()}_${safeName}`;
    const imgRef = storageRef(storage, path);
    await uploadBytes(imgRef, file);
    const url = await getDownloadURL(imgRef);
    return { url, path };
  };

  const touchSetUpdatedAt = async () => {
    if (!currentUser || !setId) return;
    const setRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}`);
    await update(setRef, { updatedAt: Date.now() });
  };

  const handleAddCard = async () => {
    if (!currentUser || !setId) return;
    if (!canSubmitNewCard) return;

    try {
      const cardsRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards`);
      const newCardRef = push(cardsRef);
      const cardId = newCardRef.key;
      if (!cardId) throw new Error('Missing card id');
      const now = Date.now();

      const computedFrontText =
        newCard.frontText.trim() ||
        (newCard.backImageFile && !newCard.frontText.trim() ? fileBaseName(newCard.backImageFile) : '') ||
        null;
      const computedBackText =
        newCard.backText.trim() ||
        (newCard.frontImageFile && !newCard.backText.trim() ? fileBaseName(newCard.frontImageFile) : '') ||
        null;

      let frontImageUrl: string | null = null;
      let frontImagePath: string | null = null;
      let backImageUrl: string | null = null;
      let backImagePath: string | null = null;

      if (newCard.frontImageFile) {
        const uploaded = await uploadCardImage(newCard.frontImageFile, 'front', cardId);
        frontImageUrl = uploaded.url;
        frontImagePath = uploaded.path;
      }

      if (newCard.backImageFile) {
        const uploaded = await uploadCardImage(newCard.backImageFile, 'back', cardId);
        backImageUrl = uploaded.url;
        backImagePath = uploaded.path;
      }

      await set(newCardRef, {
        frontText: computedFrontText,
        frontImageUrl,
        frontImagePath,
        backText: computedBackText,
        backImageUrl,
        backImagePath,
        rating: newCard.rating,
        createdAt: now,
        updatedAt: now
      });
      await touchSetUpdatedAt();
      setNewCard({ frontText: '', frontImageFile: null, backText: '', backImageFile: null, rating: 3 });
      setShowAddModal(false);
      setAlertMessage({ type: 'success', message: 'Karte hinzugefügt.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Hinzufügen der Karte.' });
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    if (!currentUser || !setId) return;
    if (!window.confirm('Karte wirklich löschen?')) return;

    try {
      const existing = cards.find((c) => c.id === cardId);
      if (existing) {
        await Promise.allSettled([
          deleteStorageObjectIfExists(existing.frontImagePath || existing.frontImageUrl),
          deleteStorageObjectIfExists(existing.backImagePath || existing.backImageUrl)
        ]);
      }
      const cardRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards/${cardId}`);
      await remove(cardRef);
      await touchSetUpdatedAt();
      setAlertMessage({ type: 'success', message: 'Karte gelöscht.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Löschen der Karte.' });
    }
  };

  const canSubmitEditCard = useMemo(() => {
    if (!editingCard) return false;
    const hasFront = (editingCard.frontText || '').trim().length > 0 || (!!(editingCard.frontImageUrl || '').trim() && !removeEditFrontImage) || !!editFrontImageFile;
    const hasBack = (editingCard.backText || '').trim().length > 0 || (!!(editingCard.backImageUrl || '').trim() && !removeEditBackImage) || !!editBackImageFile;
    return hasFront && hasBack;
  }, [editingCard, editBackImageFile, editFrontImageFile, removeEditBackImage, removeEditFrontImage]);

  const handleSaveEdit = async () => {
    if (!currentUser || !setId || !editingCard) return;
    if (!canSubmitEditCard) return;

    try {
      const now = Date.now();

      const oldFrontPath = editingCard.frontImagePath || tryExtractStoragePathFromUrl(editingCard.frontImageUrl);
      const oldBackPath = editingCard.backImagePath || tryExtractStoragePathFromUrl(editingCard.backImageUrl);

      let nextFrontImageUrl: string | null = (editingCard.frontImageUrl || '').trim() || null;
      let nextFrontImagePath: string | null = editingCard.frontImagePath || null;
      let nextBackImageUrl: string | null = (editingCard.backImageUrl || '').trim() || null;
      let nextBackImagePath: string | null = editingCard.backImagePath || null;

      let deleteOldFront = false;
      let deleteOldBack = false;

      if (editFrontImageFile) {
        const uploaded = await uploadCardImage(editFrontImageFile, 'front', editingCard.id);
        nextFrontImageUrl = uploaded.url;
        nextFrontImagePath = uploaded.path;
        deleteOldFront = !!oldFrontPath;
      } else if (removeEditFrontImage) {
        nextFrontImageUrl = null;
        nextFrontImagePath = null;
        deleteOldFront = !!oldFrontPath;
      }

      if (editBackImageFile) {
        const uploaded = await uploadCardImage(editBackImageFile, 'back', editingCard.id);
        nextBackImageUrl = uploaded.url;
        nextBackImagePath = uploaded.path;
        deleteOldBack = !!oldBackPath;
      } else if (removeEditBackImage) {
        nextBackImageUrl = null;
        nextBackImagePath = null;
        deleteOldBack = !!oldBackPath;
      }

      const cardRef = ref(database, `learning_cards/${currentUser.uid}/sets/${setId}/cards/${editingCard.id}`);
      await update(cardRef, {
        frontText: (editingCard.frontText || '').trim() || null,
        frontImageUrl: nextFrontImageUrl,
        frontImagePath: nextFrontImagePath,
        backText: (editingCard.backText || '').trim() || null,
        backImageUrl: nextBackImageUrl,
        backImagePath: nextBackImagePath,
        rating: editingCard.rating,
        updatedAt: now
      });

      if (deleteOldFront && oldFrontPath && oldFrontPath !== nextFrontImagePath) {
        await deleteStorageObjectIfExists(oldFrontPath);
      }
      if (deleteOldBack && oldBackPath && oldBackPath !== nextBackImagePath) {
        await deleteStorageObjectIfExists(oldBackPath);
      }

      await touchSetUpdatedAt();
      setEditingCard(null);
      setEditFrontImageFile(null);
      setEditBackImageFile(null);
      setRemoveEditFrontImage(false);
      setRemoveEditBackImage(false);
      setAlertMessage({ type: 'success', message: 'Karte aktualisiert.' });
      setTimeout(() => setAlertMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setAlertMessage({ type: 'danger', message: 'Fehler beim Speichern der Karte.' });
    }
  };

  if (!currentUser) {
    return (
      <Container>
        <h2 className="mb-4">Learning Cards</h2>
        <Card>
          <Card.Body>
            <p>Bitte melden Sie sich an, um Learning Cards zu verwenden.</p>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (!setId) {
    return (
      <Container>
        <Alert variant="danger">Set-ID fehlt.</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="m-0">{setMeta?.name || 'Set'}</h2>
          <div>
            <Link to="/dashboard/learning-cards" style={{ textDecoration: 'none' }}>
              ← Zurück zu Sets
            </Link>
          </div>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-success" onClick={() => navigate(`/dashboard/learning-cards/${setId}/study`)} disabled={cards.length === 0}>
            Lernen
          </Button>
          <Button variant="outline-primary" onClick={() => setShowBulkAddModal(true)}>
            Mehrere Karten erstellen
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            Neue Karte
          </Button>
        </div>
      </div>

      {alertMessage && (
        <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage(null)}>
          {alertMessage.message}
        </Alert>
      )}

      <Card>
        <Card.Body>
          {cards.length > 0 ? (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Vorderseite</th>
                  <th>Rückseite</th>
                  <th style={{ width: '140px' }}>Priorität</th>
                  <th style={{ width: '220px' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((card) => (
                  <tr key={card.id}>
                    <td>
                      {(card.frontText || '').trim() && <div className="fw-bold">{card.frontText}</div>}
                      {(card.frontImageUrl || '').trim() && (
                        <img
                          src={card.frontImageUrl}
                          alt="front"
                          style={{ maxWidth: '220px', maxHeight: '120px', objectFit: 'contain', display: 'block', marginTop: '6px' }}
                        />
                      )}
                    </td>
                    <td>
                      {(card.backText || '').trim() && <div className="fw-bold">{card.backText}</div>}
                      {(card.backImageUrl || '').trim() && (
                        <img
                          src={card.backImageUrl}
                          alt="back"
                          style={{ maxWidth: '220px', maxHeight: '120px', objectFit: 'contain', display: 'block', marginTop: '6px' }}
                        />
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const isHovered = hoveredCardRating?.cardId === card.id;
                          const previewRating = isHovered ? hoveredCardRating.rating : (card.rating || 0);
                          return (
                            <span
                              key={star}
                              onClick={() => handleUpdateCardRating(card.id, star)}
                              style={{
                                cursor: 'pointer',
                                fontSize: '1.2rem',
                                color: previewRating >= star ? '#ffc107' : '#e9ecef',
                                transition: 'color 0.15s'
                              }}
                              onMouseEnter={() => setHoveredCardRating({ cardId: card.id, rating: star })}
                              onMouseLeave={() => setHoveredCardRating(null)}
                            >
                              ★
                            </span>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setEditingCard(card);
                            setEditFrontImageFile(null);
                            setEditBackImageFile(null);
                            setRemoveEditFrontImage(false);
                            setRemoveEditBackImage(false);
                          }}
                        >
                          Bearbeiten
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={() => handleSwapFrontBack(card)}>
                          Tauschen
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteCard(card.id)}>
                          Löschen
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4">
              <div className="text-muted mb-3">Noch keine Karten in diesem Set.</div>
              <Button variant="primary" onClick={() => setShowAddModal(true)}>
                Erste Karte hinzufügen
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Neue Karte</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <div className="fw-bold mb-2">Vorderseite</div>
            <Form.Group className="mb-3">
              <Form.Label>Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newCard.frontText}
                onChange={(e) => setNewCard({ ...newCard, frontText: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bild</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0] || null;
                  setNewCard((prev) => {
                    if (!file) return { ...prev, frontImageFile: null };
                    const nextBackText = prev.backText.trim().length > 0 ? prev.backText : fileBaseName(file);
                    return { ...prev, frontImageFile: file, backText: nextBackText };
                  });
                }}
              />
              {newCard.frontImageFile && <div className="text-muted mt-1">{newCard.frontImageFile.name}</div>}
            </Form.Group>

            <hr />

            <div className="fw-bold mb-2">Rückseite (Lösung)</div>
            <Form.Group className="mb-3">
              <Form.Label>Text</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={newCard.backText}
                onChange={(e) => setNewCard({ ...newCard, backText: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Bild</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0] || null;
                  setNewCard((prev) => {
                    if (!file) return { ...prev, backImageFile: null };
                    const nextFrontText = prev.frontText.trim().length > 0 ? prev.frontText : fileBaseName(file);
                    return { ...prev, backImageFile: file, frontText: nextFrontText };
                  });
                }}
              />
              {newCard.backImageFile && <div className="text-muted mt-1">{newCard.backImageFile.name}</div>}
            </Form.Group>

            <hr />

            <div className="fw-bold mb-2">Priorität (Bewertung)</div>
            <Form.Group className="mb-3">
              <Form.Label>Bewertung (1–5)</Form.Label>
              <Form.Select
                value={newCard.rating}
                onChange={(e) => setNewCard({ ...newCard, rating: Number(e.target.value) })}
              >
                <option value={1}>1 Stern (niedrig)</option>
                <option value={2}>2 Sterne</option>
                <option value={3}>3 Sterne (mittel)</option>
                <option value={4}>4 Sterne</option>
                <option value={5}>5 Sterne (hoch)</option>
              </Form.Select>
            </Form.Group>

            {!canSubmitNewCard && (
              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                Vorderseite und Rückseite brauchen jeweils Text oder Bild.
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleAddCard} disabled={!canSubmitNewCard}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBulkAddModal} onHide={() => setShowBulkAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Mehrere Karten hinzufügen</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Bildseite</Form.Label>
              <Form.Select value={bulkSide} onChange={(e) => setBulkSide(e.target.value as 'front' | 'back')} disabled={isBulkAdding}>
                <option value="front">Vorderseite (Frage)</option>
                <option value="back">Rückseite (Lösung)</option>
              </Form.Select>
              <div className="text-muted mt-1" style={{ fontSize: '0.9rem' }}>
                Die andere Seite bekommt automatisch den Dateinamen (ohne Endung) als Text.
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Bilder</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                disabled={isBulkAdding}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const files = Array.from(e.target.files || []);
                  setBulkFiles(files);
                }}
              />
              {bulkFiles.length > 0 && <div className="text-muted mt-1">{bulkFiles.length} Dateien ausgewählt</div>}
            </Form.Group>

            {bulkProgress && (
              <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                Import: {bulkProgress.current} / {bulkProgress.total}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkAddModal(false)} disabled={isBulkAdding}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleBulkAdd} disabled={!canSubmitBulkAdd}>
            Hinzufügen
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!editingCard} onHide={() => setEditingCard(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Karte bearbeiten</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingCard && (
            <Form>
              <div className="fw-bold mb-2">Vorderseite</div>
              <Form.Group className="mb-3">
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editingCard.frontText || ''}
                  onChange={(e) => setEditingCard({ ...editingCard, frontText: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Bild</Form.Label>
                {(editingCard.frontImageUrl || '').trim() && !removeEditFrontImage && (
                  <div className="mb-2">
                    <img
                      src={editingCard.frontImageUrl}
                      alt="front"
                      style={{ maxWidth: '260px', maxHeight: '140px', objectFit: 'contain', display: 'block' }}
                    />
                    <Button
                      className="mt-2"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setRemoveEditFrontImage(true);
                        setEditFrontImageFile(null);
                      }}
                    >
                      Bild entfernen
                    </Button>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0] || null;
                    setEditFrontImageFile(file);
                    if (file) setRemoveEditFrontImage(false);
                  }}
                />
                {editFrontImageFile && <div className="text-muted mt-1">{editFrontImageFile.name}</div>}
              </Form.Group>

              <hr />

              <div className="fw-bold mb-2">Rückseite (Lösung)</div>
              <Form.Group className="mb-3">
                <Form.Label>Text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editingCard.backText || ''}
                  onChange={(e) => setEditingCard({ ...editingCard, backText: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Bild</Form.Label>
                {(editingCard.backImageUrl || '').trim() && !removeEditBackImage && (
                  <div className="mb-2">
                    <img
                      src={editingCard.backImageUrl}
                      alt="back"
                      style={{ maxWidth: '260px', maxHeight: '140px', objectFit: 'contain', display: 'block' }}
                    />
                    <Button
                      className="mt-2"
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        setRemoveEditBackImage(true);
                        setEditBackImageFile(null);
                      }}
                    >
                      Bild entfernen
                    </Button>
                  </div>
                )}
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const file = e.target.files?.[0] || null;
                    setEditBackImageFile(file);
                    if (file) setRemoveEditBackImage(false);
                  }}
                />
                {editBackImageFile && <div className="text-muted mt-1">{editBackImageFile.name}</div>}
              </Form.Group>

              <hr />

              <div className="fw-bold mb-2">Priorität (Bewertung)</div>
              <Form.Group className="mb-3">
                <Form.Label>Bewertung (1–5)</Form.Label>
                <Form.Select
                  value={editingCard.rating ?? 3}
                  onChange={(e) => setEditingCard({ ...editingCard, rating: Number(e.target.value) })}
                >
                  <option value={1}>1 Stern (niedrig)</option>
                  <option value={2}>2 Sterne</option>
                  <option value={3}>3 Sterne (mittel)</option>
                  <option value={4}>4 Sterne</option>
                  <option value={5}>5 Sterne (hoch)</option>
                </Form.Select>
              </Form.Group>

              {!canSubmitEditCard && (
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                  Vorderseite und Rückseite brauchen jeweils Text oder Bild.
                </div>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingCard(null)}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={!canSubmitEditCard}>
            Speichern
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LearningCardSet;
